import { fetcher, useClientPortalAPI } from "@certn/api";
import { Text, Stack, BasicButton, rem, showToast } from "@certn/cascadia";
import { Card, HoverInfo } from "@certn/cascadia-react";
import { css } from "@emotion/css";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { WithdrawRequestedConfirmationModal } from "./WithdrawalRequestConfirmationModal";
interface Props {
  holderName: string;
  credentials: string[] | undefined;
  caseId: string;
}

export function WithdrawalRequestCard({
  holderName,
  credentials,
  caseId,
}: Props) {
  const { t } = useTranslation();
  const [
    showWithdrawRequestedApprovalModal,
    setShowWithdrawRequestedApprovalModal,
  ] = useState(false);
  const [showWithdrawRequestedDenyModal, setShowWithdrawRequestedDenyModal] =
    useState(false);

  const { mutate } = useClientPortalAPI(
    `/api/internal/client-portal/case/${caseId}/credential-request/`
  );

  const withdrawalRequest = async () => {
    return fetcher.submit(
      `/api/internal/client-portal/case/${caseId}/credential-request/accept-withdrawal/`,
      {
        method: "POST",
      }
    );
  };

  const denyWithdrawalRequest = async () => {
    return fetcher.submit(
      `/api/internal/client-portal/case/${caseId}/credential-request/deny-withdrawal/`,
      {
        method: "POST",
      }
    );
  };

  return (
    <>
      <Card
        className={styles.withdrawalRequestCard}
        background="background-accent-neutral-weakest"
      >
        <Stack gap="space-2">
          <Stack orientation="horizontal" gap="space-2" justify="space-between">
            <Text style="global/heading-medium">
              {t(
                "{{holderName}} has requested to revoke access to the following credentials",
                { holderName }
              )}
            </Text>
            <HoverInfo
              trigger={
                <Text style="global/body-secondary-link">Learn more</Text>
              }
              infoContent={t(
                "The credential holder has requested to withdraw the credentials they have share with you. If approved, these credentials will no longer be accessible. You can either approve the request to proceed with the withdrawal or deny it to maintain access."
              )}
              testID="locked-credentials-info"
            />
          </Stack>
          <Text style="global/body-secondary">
            <Text color="text-weak">{credentials?.join(", ")}</Text>
          </Text>
          <Stack orientation="horizontal" gap="space-3">
            <BasicButton
              onPress={() => setShowWithdrawRequestedApprovalModal(true)}
            >
              {t("Approve request")}
            </BasicButton>
            <BasicButton
              onPress={() => setShowWithdrawRequestedDenyModal(true)}
            >
              {t("Deny")}
            </BasicButton>
          </Stack>
        </Stack>
      </Card>

      {showWithdrawRequestedApprovalModal && (
        <WithdrawRequestedConfirmationModal
          isOpen={showWithdrawRequestedApprovalModal}
          onClose={() => setShowWithdrawRequestedApprovalModal(false)}
          approveDenyHeader={t("Approve request")}
          approveDenyBody={t(
            "You are about to approve the withdrawal request for the following credentials:"
          )}
          onSave={() =>
            withdrawalRequest()
              .then(() => {
                mutate();
                setShowWithdrawRequestedApprovalModal(false);
              })
              .catch((error) => {
                showToast(
                  t("{{errorMessage}}", {
                    errorMessage: error.body.status[0],
                  }),
                  { type: "error" }
                );
              })
          }
          credentials={credentials}
        />
      )}

      {showWithdrawRequestedDenyModal && (
        <WithdrawRequestedConfirmationModal
          isOpen={showWithdrawRequestedDenyModal}
          onClose={() => setShowWithdrawRequestedDenyModal(false)}
          approveDenyHeader={t("Deny request")}
          approveDenyBody={t(
            "You are about to deny the withdrawal request for the following credentials:"
          )}
          onSave={() =>
            denyWithdrawalRequest()
              .then(() => {
                mutate();
                setShowWithdrawRequestedDenyModal(false);
              })
              .catch((error) => {
                showToast(
                  t("{{errorMessage}}", {
                    errorMessage: error.body.status[0],
                  }),
                  { type: "error" }
                );
              })
          }
          credentials={credentials}
        />
      )}
    </>
  );
}

const styles = {
  withdrawalRequestCard: css({
    padding: rem("space-4"),
  }),
};
